import React from 'react';
import { Box, Grid, TextField, Typography, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTheme, makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { map, forEach, findIndex, filter, isEmpty, head } from 'lodash';
import { CgCloseO, IoDocumentTextOutline } from 'react-icons/all';

const useStyles = makeStyles(() => ({
  dropzone: {
    padding: '12px',
    borderRadius: '4px',
    textAlign: 'center',
    backgroundColor: '#FAFAFA',
    border: '1px dashed #DEDEDE',
  },
  filesHeader: {
    padding: '10px',
    margin: '30px 0',
    backgroundColor: '#FAFAFA',
    borderRadius: '4px 4px 0px 0px',
    borderBottom: '1px solid #DEDEDE',
  },
}));

const Uploader = ({
  typeText,
  multiple,
  acceptFiles,
  selectedFiles,
  handleSelectedFiles,
  noEdit,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const { t } = useTranslation();

  const handleUpdateFiles = (files) => {
    if (!multiple) {
      const file = head(files);
      handleSelectedFiles([{ name: file.name, file }]);
    } else {
      const state = [...selectedFiles];
      if (isEmpty(state)) {
        forEach(files, (file) => {
          state.push({ name: file.name, file });
        });
      } else {
        forEach(files, (file) => {
          const itemIndex = findIndex(
            state,
            (item) => file.path === item.file.path,
          );
          if (itemIndex < 0) {
            state.push({ name: file.name, file });
          }
        });
      }
      handleSelectedFiles(state);
    }
  };

  const handleChangeName = (path, value) => {
    const state = [...selectedFiles];
    const fileIndex = findIndex(state, ['file.path', path]);
    state[fileIndex].name = value;

    handleSelectedFiles(state);
  };

  const handleOnDelete = (path) => {
    handleSelectedFiles(
      filter(selectedFiles, (item) => item.file.path !== path),
    );
  };

  const { getRootProps, getInputProps } = useDropzone({
    multiple,
    accept: acceptFiles,
    onDrop: (acceptedFiles) => {
      handleUpdateFiles(acceptedFiles);
    },
  });

  return (
    <Grid container>
      <Grid
        item
        md={12}
        {...getRootProps({
          className: classes.dropzone,
        })}>
        <input {...getInputProps()} />
        <Typography>{t('drop_pdf_file_here', { type: typeText })}</Typography>
        <Typography>{t('or')}</Typography>
        <Typography>
          {t('click_to')}
          <span
            style={{ color: theme.palette.primary.main, cursor: 'pointer' }}>
            &nbsp;
            {t('choose')}
          </span>
        </Typography>
      </Grid>
      <Grid item md={12} className={classes.filesHeader}>
        <Typography style={{ fontWeight: 500 }}>{t('file_name')}</Typography>
      </Grid>

      {isEmpty(selectedFiles) ? (
        <Typography
          color="textSecondary"
          style={{ fontStyle: 'italic', marginBottom: 40 }}>
          {t('no_file_added')}
        </Typography>
      ) : (
        map(selectedFiles, (item) => (
          <Grid
            item
            md={12}
            container
            alignItems="center"
            key={item.file.path}
            style={{ marginBottom: '16px' }}>
            <Box
              fontSize="20px"
              marginRight="8px"
              component={IoDocumentTextOutline}
            />
            {noEdit ? (
              <Typography> {item.name}</Typography>
            ) : (
              <TextField
                value={item.name}
                variant="outlined"
                style={{ flex: 1 }}
                label={t('file_name_visible_to_everyone')}
                onChange={(e) =>
                  handleChangeName(item.file.path, e.target.value)
                }
              />
            )}
            <IconButton
              style={{ marginLeft: 'auto' }}
              color="primary"
              aria-label="upload document"
              onClick={() => handleOnDelete(item.file.path)}>
              <Box fontSize="20px" color="#E52A58" component={CgCloseO} />
            </IconButton>
          </Grid>
        ))
      )}
    </Grid>
  );
};

Uploader.defaultProps = {
  multiple: true,
  typeText: 'PDF',
  selectedFiles: [],
  acceptFiles: 'application/pdf',
};

Uploader.propTypes = {
  multiple: PropTypes.bool,
  typeText: PropTypes.string,
  selectedFiles: PropTypes.array,
  handleSelectedFiles: PropTypes.func,
  acceptFiles: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
};

export default Uploader;
